// 排版引擎：把主題樹轉成絕對座標。座標只由這裡決定，AI 與使用者都不直接給座標。
import { type MindMap, type Structure, type Topic } from './model.js';
import { type FontSet, type TextBlock, layoutText } from './text.js';
import { type Swatch, type Theme, getTheme, resolveSwatch } from './theme.js';

export interface Point { x: number; y: number }
export type Shape = 'root' | 'box' | 'underline';
/** 1 = 往右長、-1 = 往左長、0 = 往下長（組織圖） */
export type Side = 1 | -1 | 0;

export interface LayoutNode {
  id: string;
  topic: Topic;
  depth: number;
  x: number;
  y: number;
  w: number;
  h: number;
  shape: Shape;
  side: Side;
  text: TextBlock;
  textX: number;
  textY: number;
  icon?: { name: string; x: number; y: number; size: number };
  /** 進度標籤（圓角小框＋百分比），位於標題右側 */
  progress?: { text: TextBlock; x: number; y: number; w: number; h: number };
  swatch: Swatch;
  /** 收合時隱藏的子孫數 */
  hidden: number;
  /** 組織圖中以直欄排列子節點 */
  column?: boolean;
}

export interface LayoutEdge {
  from: string;
  to: string;
  kind: 'trunk' | 'curve' | 'elbow';
  points: Point[];
  color: string;
  width: number;
}

export interface LayoutBoundary {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label?: TextBlock;
  swatch: Swatch;
}

export interface LayoutRelation {
  from: string;
  to: string;
  /** 三次貝茲曲線：起點、兩個控制點、終點 */
  points: [Point, Point, Point, Point];
  label?: TextBlock;
  labelAt: Point;
  style?: 'solid' | 'dashed';
}

export interface MapLayout {
  structure: Structure;
  theme: Theme;
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  boundaries: LayoutBoundary[];
  relations: LayoutRelation[];
  /** 包含所有內容的外框（未加邊界留白） */
  bounds: { x: number; y: number; w: number; h: number };
}

export interface LayoutOptions {
  structure?: Structure;
  theme?: string;
}

interface Style { fontSize: number; maxWidth: number; padX: number; padY: number; shape: Shape }

const ICON_SCALE = 1.3;
const ICON_GAP = 8;
const BOUNDARY_PAD = 12;
const BOUNDARY_LABEL_SIZE = 14;
const BADGE_SPACE = 22;
const PROGRESS_FONT = 12;
const PROGRESS_PAD_X = 6;
const PROGRESS_PAD_Y = 2;
const PROGRESS_GAP = 6;

// 依深度與結構決定節點外觀。
function styleFor(depth: number, structure: Structure): Style {
  if (depth === 0) return { fontSize: 26, maxWidth: 280, padX: 28, padY: 18, shape: 'root' };
  if (depth === 1) return { fontSize: 19, maxWidth: 220, padX: 18, padY: 11, shape: 'box' };
  if (structure === 'org') return { fontSize: 16, maxWidth: 180, padX: 12, padY: 8, shape: 'box' };
  return { fontSize: 16, maxWidth: 220, padX: 4, padY: 6, shape: 'underline' };
}

// 子節點之間的垂直（水平結構）或水平（組織圖）間距。
const siblingGap = (childDepth: number) => (childDepth === 1 ? 26 : 10);
// 父子之間的距離。
const levelGap = (parentDepth: number, structure: Structure) =>
  structure === 'org' ? (parentDepth === 0 ? 56 : 40) : parentDepth === 0 ? 72 : parentDepth === 1 ? 40 : 30;

// 子孫總數，用於收合徽章。
function countDescendants(topic: Topic): number {
  return topic.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

// 主要入口：計算整張圖的排版。
export function layoutMap(map: MindMap, fonts: FontSet, options: LayoutOptions = {}): MapLayout {
  const structure = options.structure ?? map.structure;
  const theme = getTheme(options.theme ?? map.theme);
  const nodes = new Map<string, LayoutNode>();
  const children = new Map<string, LayoutNode[]>();

  // 第一步：建立節點與尺寸（未定位）。
  const build = (topic: Topic, depth: number, swatch: Swatch): LayoutNode => {
    const style = styleFor(depth, structure);
    const text = layoutText(fonts, topic.title, style.fontSize, style.maxWidth);
    const iconSize = topic.icon ? Math.round(style.fontSize * ICON_SCALE) : 0;
    const iconSpace = iconSize ? iconSize + ICON_GAP : 0;
    const progressText = topic.progress !== undefined ? layoutText(fonts, `${topic.progress}%`, PROGRESS_FONT, 100) : null;
    const progress = progressText
      ? { text: progressText, x: 0, y: 0, w: Math.ceil(progressText.width + PROGRESS_PAD_X * 2), h: Math.ceil(progressText.height + PROGRESS_PAD_Y * 2) }
      : null;
    const progressSpace = progress ? progress.w + PROGRESS_GAP : 0;
    const contentH = Math.max(text.height, iconSize, progress?.h ?? 0);
    const node: LayoutNode = {
      id: topic.id, topic, depth, x: 0, y: 0,
      w: Math.ceil(text.width + iconSpace + progressSpace + style.padX * 2),
      h: Math.ceil(contentH + style.padY * 2),
      shape: style.shape, side: 1, text, textX: 0, textY: 0, swatch,
      hidden: topic.collapsed ? countDescendants(topic) : 0,
      ...(topic.icon ? { icon: { name: topic.icon, x: 0, y: 0, size: iconSize } } : {}),
      ...(progress ? { progress } : {}),
    };
    nodes.set(topic.id, node);
    const kids = topic.collapsed ? [] : topic.children.map((child, index) => {
      const branchSwatch = child.color
        ? resolveSwatch(theme, child.color)
        : depth === 0 ? theme.swatches[theme.branches[index % theme.branches.length]!]! : swatch;
      return build(child, depth + 1, branchSwatch);
    });
    children.set(topic.id, kids);
    // 組織圖中子節點全是末端時改成直欄，避免整張圖過寬。
    if (structure === 'org' && depth >= 1 && kids.length && kids.every(kid => !children.get(kid.id)!.length)) node.column = true;
    return node;
  };
  const rootSwatch = map.root.color ? resolveSwatch(theme, map.root.color) : theme.root;
  const root = build(map.root, 0, rootSwatch);

  const boundaryLabels = new Map<string, TextBlock | undefined>();
  const boundaryHead = (node: LayoutNode): number => {
    if (node.topic.boundary === undefined) return 0;
    if (!boundaryLabels.has(node.id)) boundaryLabels.set(node.id, node.topic.boundary ? layoutText(fonts, node.topic.boundary, BOUNDARY_LABEL_SIZE, 200) : undefined);
    const label = boundaryLabels.get(node.id);
    return BOUNDARY_PAD + (label ? label.height * 0.6 : 0);
  };

  // 第二步：計算子樹大小（沿堆疊方向的長度）。
  const extent = new Map<string, number>();
  const measureHorizontal = (node: LayoutNode): number => {
    const kids = children.get(node.id)!;
    const stacked = kids.reduce((sum, kid) => sum + measureHorizontal(kid), 0) + Math.max(0, kids.length - 1) * siblingGap(node.depth + 1);
    const own = node.h + (node.hidden ? 4 : 0);
    const size = Math.max(own, stacked) + (node.topic.boundary !== undefined ? boundaryHead(node) + BOUNDARY_PAD : 0);
    extent.set(node.id, size);
    return size;
  };
  const measureVertical = (node: LayoutNode): number => {
    const kids = children.get(node.id)!;
    let size: number;
    if (node.column) {
      kids.forEach(measureVertical);
      size = Math.max(node.w, 16 + Math.max(...kids.map(kid => kid.w + (kid.topic.boundary !== undefined ? BOUNDARY_PAD * 2 : 0))));
    } else {
      const stacked = kids.reduce((sum, kid) => sum + measureVertical(kid), 0) + Math.max(0, kids.length - 1) * (node.depth === 0 ? 32 : 18);
      size = Math.max(node.w, stacked);
    }
    size += node.topic.boundary !== undefined ? BOUNDARY_PAD * 2 : 0;
    extent.set(node.id, size);
    return size;
  };

  // 第三步：定位。水平結構先全部往右排，左側再鏡像。
  const placeHorizontal = (node: LayoutNode, left: number, top: number) => {
    const head = node.topic.boundary !== undefined ? boundaryHead(node) : 0;
    const innerTop = top + head;
    const innerSize = extent.get(node.id)! - (node.topic.boundary !== undefined ? head + BOUNDARY_PAD : 0);
    const innerLeft = left + (node.topic.boundary !== undefined ? BOUNDARY_PAD : 0);
    node.x = innerLeft;
    node.y = innerTop + (innerSize - node.h) / 2;
    const kids = children.get(node.id)!;
    if (!kids.length) return;
    const stacked = kids.reduce((sum, kid) => sum + extent.get(kid.id)!, 0) + (kids.length - 1) * siblingGap(node.depth + 1);
    let cursor = innerTop + (innerSize - stacked) / 2;
    const childLeft = node.x + node.w + levelGap(node.depth, structure);
    for (const kid of kids) {
      placeHorizontal(kid, childLeft, cursor);
      cursor += extent.get(kid.id)! + siblingGap(node.depth + 1);
    }
  };
  const placeVertical = (node: LayoutNode, left: number, top: number) => {
    const pad = node.topic.boundary !== undefined ? BOUNDARY_PAD : 0;
    const size = extent.get(node.id)! - pad * 2;
    // 外框往上延伸到父子間距內，節點不位移，同層節點保持對齊。
    node.y = top;
    node.side = 0;
    const kids = children.get(node.id)!;
    if (node.column) {
      node.x = left + pad;
      // 直欄：外框的上下留白也要算進間距，避免相鄰分組交疊。
      let cursor = node.y + node.h + 14;
      for (const kid of kids) {
        const hasBoundary = kid.topic.boundary !== undefined;
        cursor += hasBoundary ? boundaryHead(kid) : 0;
        placeVertical(kid, node.x + 16, cursor);
        kid.x = node.x + 16 + (hasBoundary ? BOUNDARY_PAD : 0);
        cursor += kid.h + 10 + (hasBoundary ? BOUNDARY_PAD : 0);
      }
      return;
    }
    node.x = left + pad + (size - node.w) / 2;
    if (!kids.length) return;
    const gap = node.depth === 0 ? 32 : 18;
    const stacked = kids.reduce((sum, kid) => sum + extent.get(kid.id)!, 0) + (kids.length - 1) * gap;
    let cursor = left + pad + (size - stacked) / 2;
    const childTop = node.y + node.h + levelGap(node.depth, structure);
    for (const kid of kids) {
      placeVertical(kid, cursor, childTop);
      cursor += extent.get(kid.id)! + gap;
    }
  };

  const rootKids = children.get(root.id)!;
  if (structure === 'org') {
    measureVertical(root);
    placeVertical(root, 0, 0);
  } else {
    rootKids.forEach(measureHorizontal);
    extent.set(root.id, root.h);
    let right: LayoutNode[];
    let left: LayoutNode[];
    if (structure === 'logic-right') { right = rootKids; left = []; }
    else if (structure === 'logic-left') { right = []; left = rootKids; }
    else [right, left] = splitBalanced(rootKids, kid => extent.get(kid.id)!);
    root.x = -root.w / 2;
    root.y = -root.h / 2;
    const placeSide = (side: LayoutNode[], sign: 1 | -1) => {
      const stacked = side.reduce((sum, kid) => sum + extent.get(kid.id)!, 0) + Math.max(0, side.length - 1) * siblingGap(1);
      let cursor = -stacked / 2;
      for (const kid of side) {
        placeHorizontal(kid, root.w / 2 + levelGap(0, structure), cursor);
        cursor += extent.get(kid.id)! + siblingGap(1);
        if (sign === -1) mirror(kid, children);
      }
    };
    placeSide(right, 1);
    placeSide(left, -1);
  }

  // 第四步：節點內部的文字與圖示位置。
  const ordered = orderNodes(root, children);
  for (const node of ordered) {
    const style = styleFor(node.depth, structure);
    const iconSpace = node.icon ? node.icon.size + ICON_GAP : 0;
    const progressSpace = node.progress ? node.progress.w + PROGRESS_GAP : 0;
    const contentW = node.text.width + iconSpace + progressSpace;
    const contentLeft = node.x + (node.w - contentW) / 2;
    const contentH = node.h - style.padY * 2;
    if (node.icon) {
      node.icon.x = contentLeft;
      node.icon.y = node.y + style.padY + (contentH - node.icon.size) / 2;
    }
    node.textX = contentLeft + iconSpace;
    node.textY = node.y + style.padY + (contentH - node.text.height) / 2;
    if (node.progress) {
      node.progress.x = node.textX + node.text.width + PROGRESS_GAP;
      node.progress.y = node.y + style.padY + (contentH - node.progress.h) / 2;
    }
  }

  const edges = buildEdges(root, children, structure);
  const boundaries = buildBoundaries(ordered, children, boundaryLabels);
  const relations = buildRelations(map, nodes, fonts);
  return { structure, theme, nodes: ordered, edges, boundaries, relations, bounds: computeBounds(ordered, boundaries, relations) };
}

// 依子樹大小把第 1 層分支分成左右兩側（保持原順序，右側由上而下、左側由下而上，即順時針）。
export function splitBalanced<T>(items: T[], size: (item: T) => number): [T[], T[]] {
  if (items.length <= 1) return [items, []];
  const total = items.reduce((sum, item) => sum + size(item), 0);
  let best = 1;
  let bestDiff = Infinity;
  let running = 0;
  for (let index = 1; index < items.length; index++) {
    running += size(items[index - 1]!);
    const diff = Math.abs(running - (total - running));
    if (diff < bestDiff) { bestDiff = diff; best = index; }
  }
  return [items.slice(0, best), items.slice(best).reverse()];
}

// 以 x = 0 為軸鏡像整棵子樹，並標記為向左生長。
function mirror(node: LayoutNode, children: Map<string, LayoutNode[]>) {
  node.x = -node.x - node.w;
  node.side = -1;
  for (const kid of children.get(node.id)!) mirror(kid, children);
}

// 前序列出所有節點（父節點先於子節點，繪製時線條在下）。
function orderNodes(root: LayoutNode, children: Map<string, LayoutNode[]>): LayoutNode[] {
  const list: LayoutNode[] = [];
  const visit = (node: LayoutNode) => { list.push(node); children.get(node.id)!.forEach(visit); };
  visit(root);
  return list;
}

// 子節點的連入點。
function inlet(node: LayoutNode): Point {
  if (node.side === 0) return { x: node.x + node.w / 2, y: node.y };
  const x = node.side === 1 ? node.x : node.x + node.w;
  return { x, y: node.shape === 'underline' ? node.y + node.h : node.y + node.h / 2 };
}

// 父節點的連出點。
function outlet(node: LayoutNode, structure: Structure): Point {
  if (node.shape === 'root') return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
  if (structure === 'org') return node.column ? { x: node.x + 8, y: node.y + node.h } : { x: node.x + node.w / 2, y: node.y + node.h };
  const x = node.side === 1 ? node.x + node.w : node.x;
  return { x, y: node.shape === 'underline' ? node.y + node.h : node.y + node.h / 2 };
}

// 建立父子連線：根到第 1 層為錐形主幹，其餘為曲線或直角線。
function buildEdges(root: LayoutNode, children: Map<string, LayoutNode[]>, structure: Structure): LayoutEdge[] {
  const edges: LayoutEdge[] = [];
  const visit = (node: LayoutNode) => {
    for (const kid of children.get(node.id)!) {
      const start = outlet(node, structure);
      const end = inlet(kid);
      const color = kid.swatch.stroke;
      if (node.column) {
        edges.push({ from: node.id, to: kid.id, kind: 'elbow', points: [start, { x: start.x, y: kid.y + kid.h / 2 }, { x: kid.x, y: kid.y + kid.h / 2 }], color, width: 1.5 });
      } else if (structure === 'org') {
        const middle = (start.y + end.y) / 2;
        edges.push({ from: node.id, to: kid.id, kind: node.depth === 0 ? 'trunk' : 'curve', points: [start, { x: start.x, y: middle }, { x: end.x, y: middle }, end], color, width: node.depth === 0 ? 9 : 2 });
      } else if (node.depth === 0) {
        const dx = end.x - start.x;
        edges.push({ from: node.id, to: kid.id, kind: 'trunk', points: [start, { x: start.x + dx * 0.45, y: start.y }, { x: end.x - dx * 0.35, y: end.y }, end], color, width: 10 });
      } else {
        const dx = end.x - start.x;
        edges.push({ from: node.id, to: kid.id, kind: 'curve', points: [start, { x: start.x + dx * 0.5, y: start.y }, { x: start.x + dx * 0.5, y: end.y }, end], color, width: node.depth === 1 ? 2.5 : 1.8 });
      }
      visit(kid);
    }
  };
  visit(root);
  return edges;
}

// 計算外框分組：包住該主題與所有子孫。
function buildBoundaries(nodes: LayoutNode[], children: Map<string, LayoutNode[]>, labels: Map<string, TextBlock | undefined>): LayoutBoundary[] {
  const boundaries: LayoutBoundary[] = [];
  const subtreeBox = (node: LayoutNode): { x1: number; y1: number; x2: number; y2: number } => {
    let box = { x1: node.x, y1: node.y, x2: node.x + node.w, y2: node.y + node.h };
    for (const kid of children.get(node.id)!) {
      const child = subtreeBox(kid);
      const pad = kid.topic.boundary !== undefined ? BOUNDARY_PAD : 0;
      box = { x1: Math.min(box.x1, child.x1 - pad), y1: Math.min(box.y1, child.y1 - pad), x2: Math.max(box.x2, child.x2 + pad), y2: Math.max(box.y2, child.y2 + pad) };
    }
    return box;
  };
  for (const node of nodes) {
    if (node.topic.boundary === undefined || node.depth === 0) continue;
    const box = subtreeBox(node);
    const label = labels.get(node.id);
    const head = label ? label.height * 0.6 : 0;
    boundaries.push({
      id: node.id,
      x: box.x1 - BOUNDARY_PAD, y: box.y1 - BOUNDARY_PAD - head,
      w: box.x2 - box.x1 + BOUNDARY_PAD * 2, h: box.y2 - box.y1 + BOUNDARY_PAD * 2 + head,
      ...(label ? { label } : {}), swatch: node.swatch,
    });
  }
  return boundaries;
}

// 從矩形中心朝目標方向，求與矩形邊界的交點（外推 6px 留白）。
function borderPoint(node: LayoutNode, toward: Point): Point {
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  const dx = toward.x - cx;
  const dy = toward.y - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const scale = Math.min(Math.abs((node.w / 2 + 6) / (dx || 1e-9)), Math.abs((node.h / 2 + 6) / (dy || 1e-9)));
  return { x: cx + dx * scale, y: cy + dy * scale };
}

// 建立關聯線：兩端在節點邊界上，往一側拱起的三次曲線。
function buildRelations(map: MindMap, nodes: Map<string, LayoutNode>, fonts: FontSet): LayoutRelation[] {
  const relations: LayoutRelation[] = [];
  for (const relation of map.relations) {
    const from = nodes.get(relation.from);
    const to = nodes.get(relation.to);
    if (!from || !to) continue; // 端點在收合的分支內
    const fromCenter = { x: from.x + from.w / 2, y: from.y + from.h / 2 };
    const toCenter = { x: to.x + to.w / 2, y: to.y + to.h / 2 };
    const start = borderPoint(from, toCenter);
    const end = borderPoint(to, fromCenter);
    const label = relation.label ? layoutText(fonts, relation.label, 14, 160) : undefined;
    // 兩個拱起方向 × 三種彎曲幅度，選與其他節點碰撞最少者；同分時偏好彎曲較小。
    const candidates = [0.3, 0.7, 1.2].flatMap(amount => [amount, -amount].map(bend => relationCurve(start, end, bend)));
    const obstacles = [...nodes.values()].filter(node => node !== from && node !== to);
    const cost = (curve: RelationCurve) => {
      let hits = 0;
      for (let step = 1; step < 20; step++) {
        const point = cubicAt(curve.points, step / 20);
        if (obstacles.some(node => point.x > node.x - 4 && point.x < node.x + node.w + 4 && point.y > node.y - 4 && point.y < node.y + node.h + 4)) hits++;
      }
      if (label) {
        const box = { x: curve.labelAt.x - label.width / 2 - 6, y: curve.labelAt.y - label.height / 2 - 4, w: label.width + 12, h: label.height + 8 };
        hits += 5 * obstacles.filter(node => box.x < node.x + node.w && box.x + box.w > node.x && box.y < node.y + node.h && box.y + box.h > node.y).length;
      }
      return hits;
    };
    let best = candidates[0]!;
    let bestCost = cost(best);
    for (const candidate of candidates.slice(1)) {
      const candidateCost = cost(candidate);
      if (candidateCost < bestCost) { best = candidate; bestCost = candidateCost; }
    }
    relations.push({ from: relation.from, to: relation.to, points: best.points, labelAt: best.labelAt, ...(label ? { label } : {}), ...(relation.style ? { style: relation.style } : {}) });
  }
  return relations;
}

interface RelationCurve { points: [Point, Point, Point, Point]; labelAt: Point }

// 往法線方向拱起的三次曲線；amount 為相對長度的彎曲量，正負決定側邊。標籤放在曲線中點。
function relationCurve(start: Point, end: Point, amount: number): RelationCurve {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy) || 1;
  const bend = Math.sign(amount) * Math.max(50, Math.min(220, length * Math.abs(amount)));
  const normal = { x: (-dy / length) * bend, y: (dx / length) * bend };
  const points: [Point, Point, Point, Point] = [
    start,
    { x: start.x + dx * 0.25 + normal.x, y: start.y + dy * 0.25 + normal.y },
    { x: start.x + dx * 0.75 + normal.x, y: start.y + dy * 0.75 + normal.y },
    end,
  ];
  return { points, labelAt: cubicAt(points, 0.5) };
}

// 三次貝茲曲線上 t 處的點。
export function cubicAt(points: Point[], t: number): Point {
  const [p0, p1, p2, p3] = points as [Point, Point, Point, Point];
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

// 所有內容的外框。
function computeBounds(nodes: LayoutNode[], boundaries: LayoutBoundary[], relations: LayoutRelation[]) {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  const include = (x: number, y: number, w = 0, h = 0) => {
    x1 = Math.min(x1, x); y1 = Math.min(y1, y); x2 = Math.max(x2, x + w); y2 = Math.max(y2, y + h);
  };
  for (const node of nodes) {
    include(node.x, node.y, node.w, node.h);
    if (node.hidden) {
      const badge = node.side === -1 ? node.x - BADGE_SPACE : node.side === 0 ? node.x : node.x + node.w;
      include(badge, node.y + node.h, BADGE_SPACE, node.side === 0 ? BADGE_SPACE : 0);
    }
  }
  for (const boundary of boundaries) include(boundary.x, boundary.y - 10, boundary.w, boundary.h + 10);
  for (const relation of relations) {
    for (const point of relation.points) include(point.x, point.y);
    if (relation.label) include(relation.labelAt.x - relation.label.width / 2 - 6, relation.labelAt.y - relation.label.height / 2 - 4, relation.label.width + 12, relation.label.height + 8);
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}
